import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useListFsmConnections } from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BarChart01, FileCheck02, SearchRefraction } from "@untitledui/icons";

export const Route = createFileRoute("/reporting/")({
	component: RouteComponent,
});

type Report = {
	name: string;
	description: string;
	icon: typeof BarChart01;
	to: "/reporting/cashflow" | "/reporting/payouts" | "/reporting/unpaid-completed-work";
	needsFsmConnection: boolean;
};

const reports = (
	[
		{
			name: "Cashflow Report",
			description: "Money in, money out and the end-of-day balance for every day in a selected time period. Export to CSV or PDF.",
			icon: BarChart01,
			to: "/reporting/cashflow",
			needsFsmConnection: false,
		},
		{
			name: "Payout Report",
			description: "View payouts by payee for a selected time period. Export to CSV or PDF.",
			icon: FileCheck02,
			to: "/reporting/payouts",
			needsFsmConnection: false,
		},
		{
			name: "Unpaid Completed Work",
			description: "Every job your field service software completed in a date range that has not been paid, with why and what to do about it. Export to CSV or PDF.",
			icon: SearchRefraction,
			to: "/reporting/unpaid-completed-work",
			needsFsmConnection: true,
		},
	] satisfies Report[]
).sort((first, second) => first.name.localeCompare(second.name));

function RouteComponent() {
	const navigate = useNavigate();
	const { data: connectionsResponse } = useListFsmConnections();
	const hasFsmConnection = (connectionsResponse?.data ?? []).length > 0;
	const offeredReports = reports.filter((report) => hasFsmConnection || !report.needsFsmConnection);

	return (
		<AdminLayout isWide>
			<AdminPageHeader title="Reporting" />
			<PageContentContainer className="space-y-4">
				{offeredReports.map((report) => (
					<button
						key={report.to}
						type="button"
						className="flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-4 text-left hover:bg-gray-50 cursor-pointer"
						onClick={() => navigate({ to: report.to })}
					>
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50">
							<report.icon className="h-5 w-5 text-brand-600" />
						</div>
						<div className="flex flex-col">
							<span className="text-sm font-semibold text-gray-900">{report.name}</span>
							<span className="text-sm text-gray-500">{report.description}</span>
						</div>
					</button>
				))}
			</PageContentContainer>
		</AdminLayout>
	);
}